import { useEffect, useCallback } from 'react';
import { webSocketService } from '../services/websocket';
import { notificationService } from '../services/notifications';
import {
  FileStatusUpdate,
  ExportCompletedUpdate,
  WebSocketMessage,
} from '../types';

interface UseWebSocketProps {
  onFileStatusChange?: (update: FileStatusUpdate) => void;
  onExportCompleted?: (update: ExportCompletedUpdate) => void;
  autoConnect?: boolean;
}

export const useWebSocket = ({
  onFileStatusChange,
  onExportCompleted,
  autoConnect = true,
}: UseWebSocketProps = {}) => {
  const handleMessage = useCallback(
    (data: WebSocketMessage) => {
      if (data.type === 'file_status_changed') {
        // Show notification for status changes
        const { status, message, file } = data;

        switch (status) {
          case 'ready':
            notificationService.success(`${file.name}: ${message}`);
            break;
          case 'failed':
            notificationService.error(`${file.name}: ${message}`);
            break;
          case 'processing':
            notificationService.info(`${file.name}: ${message}`);
            break;
        }

        // Call custom handler if provided
        onFileStatusChange?.(data);
      } else if (data.type === 'export_completed') {
        // Show permanent notification with download link for export completion
        notificationService.exportCompleted(
          data.message,
          data.download_url,
          data.filename
        );

        // Call custom handler if provided
        onExportCompleted?.(data);
      }
    },
    [onFileStatusChange, onExportCompleted]
  );

  useEffect(() => {
    if (autoConnect) {
      webSocketService.connect();
    }

    webSocketService.addMessageHandler(handleMessage);

    return () => {
      webSocketService.removeMessageHandler(handleMessage);
    };
  }, [handleMessage, autoConnect]);

  const connect = useCallback(() => {
    webSocketService.connect();
  }, []);

  const disconnect = useCallback(() => {
    webSocketService.disconnect();
  }, []);

  const isConnected = useCallback(() => {
    return webSocketService.isConnected();
  }, []);

  const reconnect = useCallback(() => {
    webSocketService.reconnect();
  }, []);

  return {
    connect,
    disconnect,
    isConnected,
    reconnect,
  };
};
